import { Injectable } from "@angular/core";

import { isLogentriesRemoteLoggingEnabled } from "@dffedb/util";

import { LogService } from "../log/log.service";

@Injectable({ providedIn: "root" })
export class GlobalErrorListenerService {
    private initialized = false;
    private originalConsoleError = console.error.bind(console);
    private originalConsoleLog = console.log.bind(console);
    private isLoggingConsoleError = false;

    constructor(private readonly logService: LogService) {}

    public init(): void {
        if (this.initialized) {
            return;
        }

        this.initialized = true;
        this.attachWindowErrorListener();
        this.attachUnhandledRejectionListener();
        this.patchConsoleError();
        this.registerTestHooks();
    }

    private attachWindowErrorListener(): void {
        window.addEventListener("error", (event: ErrorEvent) => {
            // Ignore synthetic events without payload to avoid noisy logs.
            if (!event.error && !event.message) {
                return;
            }

            const errorToLog = event.error || new Error(event.message || "Ukendt fejl");
            this.safeLog(errorToLog, "window.error");
        });
    }

    private attachUnhandledRejectionListener(): void {
        window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
            const reason = event.reason instanceof Error ? event.reason : new Error(this.stringify(event.reason));
            this.safeLog(reason, "window.unhandledrejection");
        });
    }

    private patchConsoleError(): void {
        console.error = (...args: any[]): void => {
            this.originalConsoleError(...args);

            if (this.isLoggingConsoleError) {
                return;
            }

            this.isLoggingConsoleError = true;
            try {
                const payload = args.length === 1 ? args[0] : args;
                const error = payload instanceof Error ? payload : new Error(this.stringify(payload));
                this.safeLog(error, "console.error");
            } finally {
                this.isLoggingConsoleError = false;
            }
        };
    }

    private registerTestHooks(): void {
        const target = globalThis as any;

        target.eforsyningTestLogentriesError = (message = "Test-fejl til Logentries"): void => {
            // Use async throw so it reliably goes through window.error.
            setTimeout(() => {
                throw new Error(message);
            }, 0);
        };

        target.eforsyningTestLogentriesConsoleError = (message = "Test console.error til Logentries"): void => {
            console.error(new Error(message));
        };

        target.eforsyningTestLogentriesUnhandledRejection = (message = "Test unhandledrejection til Logentries"): void => {
            Promise.reject(new Error(message));
        };

        // Backward compatible aliases for callers using window explicitly.
        (window as any).eforsyningTestLogentriesError = target.eforsyningTestLogentriesError;
        (window as any).eforsyningTestLogentriesConsoleError = target.eforsyningTestLogentriesConsoleError;
        (window as any).eforsyningTestLogentriesUnhandledRejection = target.eforsyningTestLogentriesUnhandledRejection;

        this.originalConsoleLog("[GlobalErrorListener] Test hooks registered:");
        this.originalConsoleLog(`- globalThis.eforsyningTestLogentriesError: ${typeof target.eforsyningTestLogentriesError}`);
        this.originalConsoleLog(`- globalThis.eforsyningTestLogentriesConsoleError: ${typeof target.eforsyningTestLogentriesConsoleError}`);
        this.originalConsoleLog(`- globalThis.eforsyningTestLogentriesUnhandledRejection: ${typeof target.eforsyningTestLogentriesUnhandledRejection}`);
    }

    private safeLog(error: any, source: string): void {
        if (!isLogentriesRemoteLoggingEnabled()) {
            // På localhost m.m. vises fejlen stadig via browser/console (fx originalConsoleError, uncaught i konsollen).
            return;
        }

        const message = error instanceof Error ? error.message : this.stringify(error);
        this.originalConsoleLog(`[GlobalErrorListener] Logging error to Logentries (${source}): ${message}`);

        this.logService.logError(error).catch(() => {
            // Avoid infinite recursion if logging itself fails.
        });
    }

    private stringify(value: any): string {
        if (typeof value === "string") {
            return value;
        }

        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
}
